import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Add,
    Coin1,
    Edit2,
    Eye,
    Lock1,
    Refresh,
    SearchNormal1,
    Trash,
    WalletMinus,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import DBButton from "../../component/global/DBButton";
import DBConfirmModal from "../../component/global/DBConfirmModal";
import CampaignDetail from "./CampaignDetail";
import CampaignWizard from "./CampaignWizard";
import CoinPurchaseModal from "./CoinPurchaseModal";
import { errorMessage, incentivesApi } from "./api";
import { displayStatus, formatNumber, normalizeSection } from "./helpers";
import "./styles.scss";

const initialData = { rows: [], count: 0 };

const BUDGET_METRICS = [
    { key: "available", icon: Coin1 },
    { key: "reserved", icon: Lock1 },
    { key: "spent", icon: WalletMinus },
];

function StateMessage({ loading, error, empty, onRetry }) {
    const { t } = useTranslation();
    if (loading) {
        return (
            <div className="incentive-state" role="status" aria-live="polite">
                <span className="incentive-spinner" aria-hidden="true" />
                {t("loading")}
            </div>
        );
    }
    if (error) {
        return (
            <div className="incentive-state incentive-state-error" role="alert">
                <p>{error}</p>
                <button type="button" onClick={onRetry}>
                    <Refresh size={18} aria-hidden="true" />
                    {t("incentives.retry")}
                </button>
            </div>
        );
    }
    if (empty) {
        return (
            <div className="incentive-state">
                <p>{t("incentives.empty")}</p>
            </div>
        );
    }
    return null;
}

function Status({ value }) {
    const { t } = useTranslation();
    const status = displayStatus(value);
    return (
        <span className={`incentive-status incentive-status-${status}`}>
            {t(`incentives.status.${status}`, { defaultValue: status })}
        </span>
    );
}

export default function IncentivesDashboard() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const section = normalizeSection(searchParams.get("section"));
    const [dashboard, setDashboard] = useState(null);
    const [list, setList] = useState(initialData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filters, setFilters] = useState({ search: "", status: "" });
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardCampaignId, setWizardCampaignId] = useState(null);
    const [detailCampaignId, setDetailCampaignId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [purchaseOpen, setPurchaseOpen] = useState(false);
    const [decisionLoading, setDecisionLoading] = useState(null);

    const setSection = (next) => {
        const params = new URLSearchParams(searchParams);
        params.set("section", next);
        setSearchParams(params, { replace: true });
    };
    const load = useCallback(() => {
        setLoading(true);
        setError("");
        const request =
            section === "overview"
                ? incentivesApi.dashboard()
                : section === "campaigns"
                ? incentivesApi.campaigns(filters)
                : section === "review"
                ? incentivesApi.reviews(filters)
                : incentivesApi.ledger(filters);
        request
            .then((data) => {
                if (section === "overview") setDashboard(data);
                else setList(data);
            })
            .catch((requestError) =>
                setError(errorMessage(requestError, t("incentives.error.load")))
            )
            .finally(() => setLoading(false));
    }, [filters, section, t]);

    useEffect(() => {
        load();
    }, [load]);

    // Zarinpal redirects back with ?Authority=...&Status=OK|NOK; settle the
    // purchase server-side, then strip the params so refresh stays idempotent.
    useEffect(() => {
        const authority = searchParams.get("Authority");
        const gatewayStatus = searchParams.get("Status");
        if (!authority) return;
        const params = new URLSearchParams(searchParams);
        params.delete("Authority");
        params.delete("Status");
        setSearchParams(params, { replace: true });
        if (gatewayStatus !== "OK") {
            setError(t("incentives.purchase.cancelled"));
            return;
        }
        incentivesApi
            .verifyPurchase(authority)
            .then(load)
            .catch((requestError) =>
                setError(
                    errorMessage(requestError, t("incentives.purchase.error"))
                )
            );
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const decide = (id, decision) => {
        let note = "";
        if (decision === "reject") {
            note = window.prompt(t("incentives.review.rejectReason")) || "";
            if (!note.trim()) return;
        }
        setDecisionLoading(id);
        incentivesApi
            .decideReview(id, decision, note)
            .then(load)
            .catch((requestError) =>
                setError(errorMessage(requestError, t("incentives.error.review")))
            )
            .finally(() => setDecisionLoading(null));
    };
    const requestDelete = (campaign) => {
        if (!campaign || campaign.status === "archived") return;
        setDeleteTarget({ id: campaign.id, name: campaign.name });
    };
    const confirmDelete = () => {
        if (!deleteTarget) return;
        setDeleting(true);
        incentivesApi
            .deleteCampaign(deleteTarget.id)
            .then(() => {
                if (detailCampaignId === deleteTarget.id) {
                    setDetailCampaignId(null);
                }
                setDeleteTarget(null);
                load();
            })
            .catch((requestError) =>
                setError(errorMessage(requestError, t("incentives.error.delete")))
            )
            .finally(() => setDeleting(false));
    };
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    const wallet = dashboard?.wallet || dashboard?.budget || {};
    const rows = list.rows || [];

    return (
        <div className="incentives-dashboard">
            <header className="incentive-page-header">
                <div>
                    <h1>{t("incentives.title")}</h1>
                    <span>{t("incentives.subtitle")}</span>
                </div>
                <div className="incentive-page-actions">
                    <DBButton
                        type="secondary"
                        outlined
                        caption={
                            <>
                                <Coin1 size={18} aria-hidden="true" />
                                {t("incentives.buyCoins")}
                            </>
                        }
                        onClick={() => setPurchaseOpen(true)}
                    />
                    <DBButton
                        type="primary"
                        caption={
                            <>
                                <Add size={18} aria-hidden="true" />
                                {t("incentives.create")}
                            </>
                        }
                        onClick={() => {
                            setWizardCampaignId(null);
                            setWizardOpen(true);
                        }}
                    />
                </div>
            </header>

            <nav
                className="incentive-section-tabs"
                aria-label={t("incentives.sections")}
            >
                {["overview", "campaigns", "review", "ledger"].map((item) => (
                    <button
                        key={item}
                        type="button"
                        aria-current={section === item ? "page" : undefined}
                        onClick={() => setSection(item)}
                    >
                        {t(`incentives.section.${item}`)}
                    </button>
                ))}
            </nav>

            {dashboard && (
                <section
                    className="incentive-budget-strip"
                    aria-label={t("incentives.budget.title")}
                >
                    {BUDGET_METRICS.map((metric) => {
                        const Icon = metric.icon;
                        return (
                            <div key={metric.key}>
                                <Icon size={16} aria-hidden="true" />
                                <div>
                                    <span>
                                        {t(`incentives.budget.${metric.key}`)}
                                    </span>
                                    <strong>
                                        {formatNumber(
                                            wallet[metric.key] ??
                                                wallet[`${metric.key}_coins`] ??
                                                dashboard?.[`${metric.key}_coins`],
                                            locale
                                        )}
                                        <small>{t("incentives.coins")}</small>
                                    </strong>
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}

            {section !== "overview" && (
                <form
                    className="incentive-filters"
                    role="search"
                    onSubmit={(event) => {
                        event.preventDefault();
                        load();
                    }}
                >
                    <label>
                        <span className="visually-hidden">
                            {t("incentives.filters.search")}
                        </span>
                        <SearchNormal1 size={18} aria-hidden="true" />
                        <input
                            type="search"
                            value={filters.search}
                            placeholder={t("incentives.filters.search")}
                            onChange={(event) =>
                                setFilters({
                                    ...filters,
                                    search: event.target.value,
                                })
                            }
                        />
                    </label>
                    <label>
                        <span className="visually-hidden">
                            {t("incentives.filters.status")}
                        </span>
                        <select
                            value={filters.status}
                            onChange={(event) =>
                                setFilters({
                                    ...filters,
                                    status: event.target.value,
                                })
                            }
                        >
                            <option value="">
                                {t("incentives.filters.allStatuses")}
                            </option>
                            {["active", "draft", "paused", "completed"].map(
                                (status) => (
                                    <option key={status} value={status}>
                                        {t(`incentives.status.${status}`)}
                                    </option>
                                )
                            )}
                        </select>
                    </label>
                    <button type="submit">{t("incentives.filters.apply")}</button>
                </form>
            )}

            <main className="incentive-section-content" aria-busy={loading}>
                <StateMessage
                    loading={loading}
                    error={error}
                    empty={section !== "overview" && rows.length === 0}
                    onRetry={load}
                />
                {!loading && !error && section === "overview" && (
                    <div className="incentive-overview-grid">
                        <section>
                            <h2>{t("incentives.overview.activity")}</h2>
                            <dl>
                                <div>
                                    <dt>
                                        {t("incentives.overview.activeCampaigns")}
                                    </dt>
                                    <dd>
                                        {formatNumber(
                                            dashboard?.active_campaigns,
                                            locale
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt>
                                        {t("incentives.overview.pendingReviews")}
                                    </dt>
                                    <dd>
                                        {formatNumber(
                                            dashboard?.pending_reviews,
                                            locale
                                        )}
                                    </dd>
                                </div>
                                <div>
                                    <dt>
                                        {t("incentives.overview.rewardsIssued")}
                                    </dt>
                                    <dd>
                                        {formatNumber(
                                            dashboard?.rewards_issued,
                                            locale
                                        )}
                                    </dd>
                                </div>
                            </dl>
                        </section>
                        <section>
                            <h2>{t("incentives.overview.attention")}</h2>
                            {(dashboard?.warnings || []).length ? (
                                <ul>
                                    {dashboard.warnings.map((warning, index) => (
                                        <li key={warning.id || index}>
                                            {warning.message || warning}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p>{t("incentives.overview.noWarnings")}</p>
                            )}
                        </section>
                    </div>
                )}
                {!loading && !error && section === "campaigns" && rows.length > 0 && (
                    <ResponsiveList
                        rows={rows}
                        columns={[
                            ["name", t("incentives.table.campaign")],
                            ["action_label", t("incentives.table.action")],
                            ["status", t("incentives.table.status")],
                            ["budget", t("incentives.table.budget")],
                            ["spent", t("incentives.table.spent")],
                        ]}
                        statusKey="status"
                        onOpenRow={(row) => setDetailCampaignId(row.id)}
                        renderActions={(row) => (
                            <div className="incentive-row-actions">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setDetailCampaignId(row.id)
                                    }
                                >
                                    <Eye size={16} aria-hidden="true" />
                                    {t("incentives.view")}
                                </button>
                                {row.status !== "archived" && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setWizardCampaignId(row.id);
                                                setWizardOpen(true);
                                            }}
                                        >
                                            <Edit2 size={16} aria-hidden="true" />
                                            {t("incentives.edit")}
                                        </button>
                                        <button
                                            type="button"
                                            className="is-danger"
                                            onClick={() => requestDelete(row)}
                                        >
                                            <Trash size={16} aria-hidden="true" />
                                            {t("incentives.delete")}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    />
                )}
                {!loading && !error && section === "review" && rows.length > 0 && (
                    <ResponsiveList
                        rows={rows}
                        columns={[
                            ["actor_name", t("incentives.table.user")],
                            ["campaign_name", t("incentives.table.campaign")],
                            ["evidence", t("incentives.table.evidence")],
                            ["amount", t("incentives.table.amount")],
                        ]}
                        renderActions={(row) => (
                            <div className="incentive-row-actions">
                                <button
                                    type="button"
                                    disabled={decisionLoading === row.id}
                                    onClick={() => decide(row.id, "approve")}
                                >
                                    {t("incentives.approve")}
                                </button>
                                <button
                                    type="button"
                                    className="is-danger"
                                    disabled={decisionLoading === row.id}
                                    onClick={() => decide(row.id, "reject")}
                                >
                                    {t("incentives.reject")}
                                </button>
                            </div>
                        )}
                    />
                )}
                {!loading && !error && section === "ledger" && rows.length > 0 && (
                    <ResponsiveList
                        rows={rows}
                        columns={[
                            ["created_at", t("incentives.table.date")],
                            ["description", t("incentives.table.description")],
                            ["reference", t("incentives.table.reference")],
                            ["amount", t("incentives.table.amount")],
                            ["balance", t("incentives.table.balance")],
                        ]}
                    />
                )}
            </main>
            <CampaignWizard
                show={wizardOpen}
                campaignId={wizardCampaignId}
                onHide={() => {
                    setWizardOpen(false);
                    setWizardCampaignId(null);
                }}
                onPublished={load}
            />
            <CampaignDetail
                show={Boolean(detailCampaignId)}
                campaignId={detailCampaignId}
                onHide={() => setDetailCampaignId(null)}
                onEdit={(id) => {
                    setDetailCampaignId(null);
                    setWizardCampaignId(id);
                    setWizardOpen(true);
                }}
                onDelete={requestDelete}
            />
            <DBConfirmModal
                show={Boolean(deleteTarget)}
                stacked
                onHide={() => !deleting && setDeleteTarget(null)}
                title={t("incentives.confirmDelete.title")}
                description={t("incentives.confirmDelete.description", {
                    name: deleteTarget?.name || "",
                })}
                btnTitle={t("incentives.confirmDelete.action")}
                loading={deleting}
                onConfirm={confirmDelete}
            />
            <CoinPurchaseModal
                show={purchaseOpen}
                onHide={() => setPurchaseOpen(false)}
                onPurchased={load}
            />
        </div>
    );
}

function ResponsiveList({ rows, columns, statusKey, renderActions, onOpenRow }) {
    const { t } = useTranslation();
    const value = (row, key) => {
        if (key === statusKey) return <Status value={row[key]} />;
        const cell = row[key];
        if (cell === null || cell === undefined || cell === "") return "—";
        if (typeof cell === "object") return JSON.stringify(cell);
        if (key === "name" && onOpenRow) {
            return (
                <button
                    type="button"
                    className="incentive-campaign-name"
                    onClick={() => onOpenRow(row)}
                >
                    {cell}
                </button>
            );
        }
        return cell;
    };
    return (
        <>
            <div className="incentive-table-wrap">
                <table>
                    <thead>
                        <tr>
                            {columns.map(([key, label]) => (
                                <th key={key} scope="col">
                                    {label}
                                </th>
                            ))}
                            {renderActions && (
                                <th scope="col">{t("incentives.table.actions")}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                {columns.map(([key]) => (
                                    <td key={key}>{value(row, key)}</td>
                                ))}
                                {renderActions && <td>{renderActions(row)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="incentive-mobile-list">
                {rows.map((row) => (
                    <article key={row.id}>
                        <dl>
                            {columns.map(([key, label]) => (
                                <div key={key}>
                                    <dt>{label}</dt>
                                    <dd>{value(row, key)}</dd>
                                </div>
                            ))}
                        </dl>
                        {renderActions?.(row)}
                    </article>
                ))}
            </div>
        </>
    );
}
